import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Check, X, Wrench, AlertTriangle, Plus } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * The shop — the mechanic's day (owner, Sep 29 2026: James).
 *
 * Daily / weekly / monthly checklists on the manager-check engine; the broken
 * gear the crew reported, to fix and clear; and supply requests, which land on
 * the owner's Needs Attention list until he marks them ordered.
 *
 * Daily and monthly go one item at a time. The WEEKLY list is the owner's own
 * 13 lines (Oct 6 2026) and shows whole — see WeeklyList below.
 */
const MODES = [
  { key: 'mech_daily', label: 'Today' },
  { key: 'mech_weekly', label: 'Weekly' },
  { key: 'mech_monthly', label: 'Monthly' },
] as const;

const fmtDay = (iso?: string | null) => {
  const d = String(iso || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
};

// Needs Attention sends the owner straight to the week's list: /mechanic?tab=weekly.
const TAB_FROM_LINK: Record<string, string> = { weekly: 'mech_weekly', monthly: 'mech_monthly', requests: 'requests', gear: 'gear' };
// Who may wipe the week's or month's check. Not the mechanic: a half-done week
// is the record of the week (owner, Oct 6 2026) — he changes an answer instead.
const LONG_MODES = ['mech_weekly', 'mech_monthly'];

export default function MechanicHome() {
  const readOnly = useReadOnly();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const linked = TAB_FROM_LINK[params.get('tab') || ''] || '';
  const [tab, setTab] = useState<string>(linked || 'mech_daily');
  // "Open the shop list" on the mechanic's Employee side switches hats and then
  // lands on ?tab=weekly. If the hat switch gets him to /mechanic a beat before
  // the link does, the page was already up on Today and stayed there — so a
  // tab named in the link is followed whenever it arrives, not only at first.
  useEffect(() => { if (linked) setTab(linked); }, [linked]);
  const isCheck = MODES.some(m => m.key === tab);
  const office = user?.role === 'admin' || user?.role === 'manager';

  const { data, isLoading } = useQuery(['mechanic', isCheck ? tab : 'mech_daily'],
    () => api.get('/mechanic', { params: { mode: isCheck ? tab : 'mech_daily' } }).then(r => r.data));

  const refresh = () => qc.invalidateQueries('mechanic');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1">
        {MODES.map(m => (
          <button key={m.key} onClick={() => setTab(m.key)}
            className={`text-sm font-semibold px-3 py-2 rounded-lg border ${tab === m.key ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-300 text-gray-700'}`}>
            {m.label}{data?.done_today?.[m.key] ? ' ✓' : ''}
          </button>
        ))}
        <button onClick={() => setTab('requests')}
          className={`text-sm font-semibold px-3 py-2 rounded-lg border ${tab === 'requests' ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-300 text-gray-700'}`}>
          Requests
        </button>
        <button onClick={() => setTab('gear')}
          className={`text-sm font-semibold px-3 py-2 rounded-lg border ${tab === 'gear' ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-300 text-gray-700'}`}>
          Broken gear
        </button>
      </div>

      {isCheck && (isLoading ? <p className="text-sm text-gray-500">Loading…</p>
        : data?.check?.every_line
          ? <WeeklyList check={data.check} history={data?.history || []} readOnly={readOnly} canStartOver={office} />
          : <Checklist mode={tab} check={data?.check} history={data?.history || []} onChange={refresh} readOnly={readOnly}
              canStartOver={office || !LONG_MODES.includes(tab)} />)}
      {tab === 'requests' && <Requests readOnly={readOnly} />}
      {tab === 'gear' && <BrokenGear readOnly={readOnly} />}
    </div>
  );
}

function Checklist({ mode, check, history, onChange, readOnly, canStartOver }: {
  mode: string; check: any; history: any[]; onChange: () => void; readOnly: boolean; canStartOver: boolean;
}) {
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const answer = useMutation((body: { key: string; result: string; note?: string }) =>
    api.post(`/mechanic/${check.id}/answer`, body).then(r => r.data), {
    onSuccess: (d: any) => { if (d?.said) toast.success(d.said); setNoteFor(null); setNote(''); onChange(); },
    // 409 = this phone is showing a check that is no longer the month's one; load the one that is.
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark that'); if (e?.response?.status === 409) onChange(); },
  });
  const passRest = useMutation(() => api.post(`/mechanic/${check.id}/pass-rest`), {
    onSuccess: () => { toast.success('Rest marked done'); onChange(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark those'); },
  });
  const restart = useMutation(() => api.post('/mechanic/start', { mode, restart: true }), {
    onSuccess: () => { toast.success('Started over'); onChange(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not restart'); },
  });

  if (!check) return <p className="text-sm text-gray-500">No check yet.</p>;
  const next = check.next;
  const items: any[] = check.items || [];
  const done = check.status === 'complete';

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 font-mono tracking-widest">{check.progress?.dots}</p>

      {done ? (
        <div className={`card p-4 space-y-1 border-2 ${check.summary?.held ? 'border-amber-400' : 'border-green-400'}`}>
          <p className="font-bold text-gray-900">{check.summary?.held ? `Done — ${check.summary.held} on HOLD` : 'All done ✓'}</p>
          {check.summary?.holds?.map((h: string, i: number) => <p key={i} className="text-sm text-amber-800">✗ {h}</p>)}
          {!readOnly && canStartOver && <button onClick={() => restart.mutate()} disabled={restart.isLoading} className="text-xs text-gray-500 underline">Start over</button>}
        </div>
      ) : next ? (
        <div className="card p-4 space-y-3 border-2 border-brand-300">
          <div>
            <p className="text-xs text-gray-500">{check.progress?.answered + 1} of {check.progress?.total}</p>
            <p className="text-lg font-bold text-gray-900">{next.label}</p>
            {next.detail && <p className="text-sm text-gray-600 mt-0.5">{next.detail}</p>}
          </div>
          {noteFor === next.key ? (
            <div className="space-y-2">
              <input value={note} onChange={e => setNote(e.target.value)} autoFocus placeholder="What's wrong / what's needed"
                className="input w-full text-sm" />
              <div className="flex gap-2">
                <button onClick={() => answer.mutate({ key: next.key, result: 'hold', note })} disabled={answer.isLoading}
                  className="flex-1 py-2.5 rounded-lg bg-amber-500 text-white text-sm font-semibold disabled:opacity-50">Hold — needs work</button>
                <button onClick={() => answer.mutate({ key: next.key, result: 'pass', note })} disabled={answer.isLoading}
                  className="flex-1 py-2.5 rounded-lg bg-green-600 text-white text-sm font-semibold disabled:opacity-50">Done, with note</button>
              </div>
              <button onClick={() => { setNoteFor(null); setNote(''); }} className="text-xs text-gray-500 underline">Cancel</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => answer.mutate({ key: next.key, result: 'pass' })} disabled={answer.isLoading || readOnly}
                className="flex-1 py-3 rounded-lg bg-green-600 text-white text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50">
                <Check className="h-4 w-4" /> Done
              </button>
              <button onClick={() => setNoteFor(next.key)} disabled={readOnly}
                className="px-4 py-3 rounded-lg border border-amber-400 text-amber-700 text-sm font-semibold disabled:opacity-50">Problem</button>
              <button onClick={() => answer.mutate({ key: next.key, result: 'skip' })} disabled={answer.isLoading || readOnly}
                className="px-4 py-3 rounded-lg border border-gray-300 text-gray-600 text-sm disabled:opacity-50">Skip</button>
            </div>
          )}
          {check.progress?.answered > 0 && !readOnly && (
            <button onClick={() => passRest.mutate()} disabled={passRest.isLoading} className="text-xs text-gray-500 underline">Rest are all good</button>
          )}
        </div>
      ) : null}

      {/* The whole list, with what's been said */}
      <div className="card divide-y divide-gray-100 p-0">
        {items.map(i => (
          <div key={i.key} className="px-3 py-2 flex items-start gap-2 text-sm">
            <span className={`mt-0.5 ${i.result === 'pass' ? 'text-green-600' : i.result === 'hold' ? 'text-amber-600' : i.result === 'skip' ? 'text-gray-400' : 'text-gray-300'}`}>
              {i.result === 'pass' ? '✓' : i.result === 'hold' ? '✗' : i.result === 'skip' ? '–' : '□'}
            </span>
            <div className="min-w-0">
              <p className={i.result ? 'text-gray-500' : 'text-gray-900 font-medium'}>{i.label}</p>
              {i.note && <p className="text-xs text-amber-700">{i.note}</p>}
            </div>
          </div>
        ))}
      </div>

      {history.length > 0 && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer font-semibold">Past checks</summary>
          <ul className="mt-1 space-y-0.5">{history.map(h => <li key={h.id}>{h.log}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

/**
 * The shop weekly — the owner's list, his words (Oct 6 2026): "Once a week,
 * send the mechanic this list and require a done / not done / note on every
 * line. Do not add tasks. Do not skip lines… Output: a checklist with a
 * checkbox and a note field on each line, plus a parts-request box at the
 * bottom. Flag any line left blank."
 *
 * So this is the whole list at once, not one line at a time:
 *   - every line has a big Done and a big Not done, and a note field;
 *   - there is no Skip and no "rest are all good" (the server refuses both);
 *   - Not done needs the note — why, or what is needed; the hour-meter line
 *     needs the hours even when it is Done;
 *   - a line with no answer says "blank" in amber, and the top counts them;
 *   - the last line (parts, tools, supplies) is answered from the box at the
 *     bottom: send a request, or tap "Nothing needed this week".
 * It is the WEEK's list: open it Thursday and Tuesday's answers are there. An
 * answer can be changed until Sunday night — but never by one stray tap: the
 * two buttons sit side by side under a greasy thumb, so changing an answer
 * that is already saved waits for "Save" (see tap below).
 */
// Answered = Done or Not done. Anything else on a line (nothing, or a 'skip'
// left from before "Do not skip lines") is a blank line.
const answered = (i: any) => i?.result === 'pass' || i?.result === 'hold';
const startsWords = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

function WeeklyList({ check, history, readOnly, canStartOver }: {
  check: any; history: any[]; readOnly: boolean; canStartOver: boolean;
}) {
  const qc = useQueryClient();
  // What he has typed but not sent, per line; and a Done / Not done he tapped
  // that is waiting on its note.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [want, setWant] = useState<Record<string, 'pass' | 'hold'>>({});
  const [wiping, setWiping] = useState(false);
  const forget = (key: string) => {
    setDraft(d => { const n = { ...d }; delete n[key]; return n; });
    setWant(w => { const n = { ...w }; delete n[key]; return n; });
  };

  const answer = useMutation((body: { key: string; result: 'pass' | 'hold'; note?: string; quiet?: boolean }) =>
    api.post(`/mechanic/${check.id}/answer`, { key: body.key, result: body.result, note: body.note }).then(r => r.data), {
    onSuccess: (d: any, body) => {
      // A green tick beside "NOT DONE" reads wrong at a glance.
      if (d?.said && !body.quiet) (body.result === 'hold' ? toast.warn : toast.success)(d.said);
      forget(body.key);
      // Show the answer now; the lines not on yet ("Starting next week") only come with a full load.
      qc.setQueryData(['mechanic', 'mech_weekly'], (old: any) => (old ? { ...old, check: { ...d.check, coming: old.check?.coming } } : old));
      qc.invalidateQueries('mechanic');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Could not mark that');
      // 409 = this phone is showing a sheet that is no longer the week's list. Fetch the one that is.
      if (e?.response?.status === 409) qc.invalidateQueries('mechanic');
    },
  });
  const startOver = useMutation(() => api.post('/mechanic/start', { mode: 'mech_weekly', restart: true }), {
    onSuccess: () => { toast.success('This week is blank again'); setWiping(false); setDraft({}); setWant({}); qc.invalidateQueries('mechanic'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not start over'); },
  });

  const items: any[] = check.items || [];
  const c = check.counts || { total: items.length, answered: 0, not_done: 0, blank: items.length };
  const allIn = c.total > 0 && c.blank === 0;
  const parts = items.find(i => i.key === check.parts_key) || null;
  // His list in his order — the lines not on yet sit in their own place, greyed.
  const rows: any[] = [...items.map((i, idx) => ({ ...i, n: i.n ?? idx + 1 })), ...(check.coming || []).map((x: any) => ({ ...x, coming: true }))]
    .sort((a, b) => a.n - b.n);

  const noteOf = (i: any) => (draft[i.key] !== undefined ? draft[i.key] : (i.note || ''));
  const needsNote = (i: any, result: 'pass' | 'hold') => result === 'hold' || !!i.note_required;
  const tap = (i: any, result: 'pass' | 'hold') => {
    const note = noteOf(i).trim();
    // Changing an answer that is already saved never goes through on the tap
    // alone. One slip onto "Not done" used to turn a Done line's note ("big
    // mower burned half a quart") into the reason it was NOT done and put it on
    // the owner's list; tapping Done to undo it then wiped the note he asked
    // for ("Note any that burned oil"). So the tap is held, the note stays in
    // the field for him to keep, fix or clear, and nothing is saved until
    // "Save". Cancel leaves the line exactly as it was.
    const flip = answered(i) && i.result !== result;
    if (flip || (needsNote(i, result) && !note)) {
      // Hold the tap, put him in the note field; "Save" finishes it. The focus
      // happens inside the tap itself — an iPhone only raises the keyboard for
      // a focus that comes straight from a touch.
      setWant(w => ({ ...w, [i.key]: result }));
      document.getElementById(`wk-note-${i.key}`)?.focus();
      return;
    }
    answer.mutate({ key: i.key, result, note: note || undefined });
  };

  const sendParts = (note: string) => { if (parts) answer.mutate({ key: parts.key, result: 'pass', note, quiet: true }); };
  const asked = parts?.note && String(parts.note).startsWith('Asked for: ') ? String(parts.note).slice(11) : '';

  return (
    <div className="space-y-3">
      <div className={`card p-4 border-2 ${allIn ? (c.not_done ? 'border-red-300' : 'border-green-400') : 'border-gray-200'}`}>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Shop weekly · {check.week_label}</p>
        <p className="text-lg font-bold text-gray-900 leading-snug mt-0.5">
          {c.answered} of {c.total} answered
          <span className={c.not_done ? 'text-red-700' : 'text-gray-500'}> · {c.not_done} not done</span>
          <span className={c.blank ? 'text-amber-700' : 'text-gray-500'}> · {c.blank} blank</span>
        </p>
        {allIn && <p className="text-base font-bold text-green-700 mt-1">This week's list is in.</p>}
        {!allIn && <p className="text-sm text-gray-600 mt-1">Every line gets Done or Not done. Not done needs a note.</p>}
      </div>

      {rows.map(i => {
        if (i.coming) {
          return (
            <div key={`coming-${i.key}`} className="card p-3 border-l-4 border-gray-200 opacity-60">
              <div className="flex items-start gap-2">
                <span className="text-base font-bold text-gray-400 w-7 flex-shrink-0">{i.n}</span>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-gray-500 leading-snug">{i.label}</p>
                  <p className="text-sm text-gray-500 mt-0.5">Starts the week of {startsWords(i.starts)}.</p>
                </div>
              </div>
            </div>
          );
        }
        const isParts = parts && i.key === parts.key;
        const pending = want[i.key];
        const note = noteOf(i);
        const changed = draft[i.key] !== undefined && draft[i.key].trim() !== (i.note || '');
        // What "Save" would send: the tap that is waiting, else the answer already on the line.
        const saveAs: 'pass' | 'hold' | null = pending || (changed && answered(i) ? i.result : null);
        // A held change of a saved answer, with the old note still in the field.
        const flipping = !!pending && answered(i) && i.result !== pending && !!note.trim();
        // The held tap cannot be saved until the note is typed (Not done, or the hour meter).
        const noteMissing = !!pending && !note.trim() && needsNote(i, pending);
        const canSave = !!saveAs && !(needsNote(i, saveAs) && !note.trim());
        const save = () => { if (saveAs && canSave) answer.mutate({ key: i.key, result: saveAs, note: note.trim() || undefined }); };
        return (
          <div key={i.key} className={`card p-3 border-l-4 ${i.result === 'pass' ? 'border-green-500' : i.result === 'hold' ? 'border-red-500' : 'border-amber-400'}`}>
            {/* amber = blank: no answer yet (or an old 'skip', which is not one) */}
            <div className="flex items-start gap-2">
              <span className="text-base font-bold text-gray-500 w-7 flex-shrink-0">{i.n}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-base font-semibold text-gray-900 leading-snug">{i.label}</p>
                  {!answered(i) && <span className="flex-shrink-0 text-[11px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">blank</span>}
                </div>
                {i.detail && <p className="text-sm text-gray-600 mt-0.5 leading-snug">{i.detail}</p>}
              </div>
            </div>

            {isParts ? (
              <p className={`mt-2 text-sm font-semibold ${answered(i) ? 'text-green-700' : 'text-amber-800'}`}>
                {answered(i) ? `Done${i.note ? ` — ${i.note}` : ''}` : 'Send a request below, or tap “Nothing needed this week”.'}
              </p>
            ) : (
              <>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button onClick={() => tap(i, 'pass')} disabled={readOnly || answer.isLoading}
                    className={`py-3.5 rounded-lg text-base font-bold flex items-center justify-center gap-1.5 border-2 border-green-600 disabled:opacity-50 ${i.result === 'pass' && !pending ? 'bg-green-600 text-white' : pending === 'pass' ? 'bg-green-50 text-green-800' : 'bg-white text-green-700'}`}>
                    <Check className="h-5 w-5" /> Done
                  </button>
                  <button onClick={() => tap(i, 'hold')} disabled={readOnly || answer.isLoading}
                    className={`py-3.5 rounded-lg text-base font-bold flex items-center justify-center gap-1.5 border-2 border-red-600 disabled:opacity-50 ${i.result === 'hold' && !pending ? 'bg-red-600 text-white' : pending === 'hold' ? 'bg-red-50 text-red-800' : 'bg-white text-red-700'}`}>
                    <X className="h-5 w-5" /> Not done
                  </button>
                </div>
                <input id={`wk-note-${i.key}`} value={note} disabled={readOnly} maxLength={200} enterKeyHint="done"
                  onChange={e => setDraft(d => ({ ...d, [i.key]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') save(); }}
                  placeholder={i.note_required ? 'Hours on the meter' : (pending || i.result) === 'hold' ? 'Why / what is needed' : 'Note'}
                  className={`input w-full text-base mt-2 ${noteMissing ? 'border-amber-500 ring-1 ring-amber-400' : ''}`} />
                {noteMissing && (
                  <p className="text-sm font-semibold text-amber-800 mt-1">
                    {pending === 'hold' ? 'Not done needs a note — why, or what is needed.' : (i.detail || 'This line needs a note.')}
                  </p>
                )}
                {flipping && (
                  <div className="mt-1 flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-amber-800">
                      {pending === 'hold' ? 'Not saved yet. Make the note say why, then Save.' : 'Not saved yet. Fix or clear the old note, then Save.'}
                    </p>
                    {pending === 'pass' && !i.note_required && (
                      <button onClick={() => setDraft(d => ({ ...d, [i.key]: '' }))}
                        className="flex-shrink-0 px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">Clear note</button>
                    )}
                  </div>
                )}
                {saveAs && (
                  <div className="mt-2 flex gap-2">
                    <button onClick={save} disabled={!canSave || answer.isLoading || readOnly}
                      className="flex-1 py-3 rounded-lg bg-gray-900 text-white text-base font-bold disabled:opacity-40">
                      {pending ? `Save — ${pending === 'hold' ? 'Not done' : 'Done'}` : 'Save note'}
                    </button>
                    <button onClick={() => forget(i.key)} className="px-4 py-3 rounded-lg border border-gray-300 text-sm text-gray-600">Cancel</button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* His "parts-request box at the bottom" — the same form as the Requests tab. */}
      {parts && (
        <div className="space-y-2 pt-2">
          <h2 className="text-lg font-bold text-gray-900">Parts, tools or supplies needed</h2>
          <Requests readOnly={readOnly} compact
            onSent={(item, qty) => sendParts(`Asked for: ${[asked, `${item}${qty ? ` × ${qty}` : ''}`].filter(Boolean).join(', ')}`.slice(0, 200))}
            afterForm={asked ? (
              <p className="text-sm font-semibold text-green-700">Line {parts.n ?? items.length} is done — asked for: {asked}</p>
            ) : (
              <button onClick={() => sendParts('Nothing needed')} disabled={readOnly || answer.isLoading}
                className={`w-full py-3.5 rounded-lg text-base font-bold border-2 border-gray-700 disabled:opacity-50 ${parts.result === 'pass' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
                {parts.result === 'pass' ? '✓ Nothing needed this week' : 'Nothing needed this week'}
              </button>
            )} />
        </div>
      )}

      {allIn && (
        <div className={`card p-4 border-2 ${c.not_done ? 'border-red-300' : 'border-green-400'}`}>
          <p className="text-base font-bold text-green-700">This week's list is in.</p>
          <p className="text-sm text-gray-600">
            {c.not_done ? `${c.not_done} not done — the office sees ${c.not_done === 1 ? 'it' : 'them'} with your note. ` : ''}You can change an answer until Sunday night.
          </p>
        </div>
      )}

      {canStartOver && !readOnly && (wiping ? (
        <div className="card p-3 space-y-2 border border-red-300">
          <p className="text-sm font-semibold text-gray-900">Wipe every answer on this week's list?</p>
          <div className="flex gap-2">
            <button onClick={() => startOver.mutate()} disabled={startOver.isLoading} className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-sm font-bold disabled:opacity-50">Yes, start the week over</button>
            <button onClick={() => setWiping(false)} className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-600">Keep it</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setWiping(true)} className="text-xs text-gray-500 underline">Start this week over</button>
      ))}

      {history.length > 0 && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer font-semibold">Past checks</summary>
          <ul className="mt-1 space-y-0.5">{history.map(h => <li key={h.id}>{h.log}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

const STATUS_WORDS: Record<string, [string, string]> = {
  open: ['Waiting', 'bg-amber-100 text-amber-800'],
  ordered: ['Ordered', 'bg-blue-100 text-blue-800'],
  got_it: ['In the shop', 'bg-green-100 text-green-800'],
  declined: ['Not now', 'bg-gray-100 text-gray-600'],
};

/**
 * "Need something?" — the one supply-request form. The Requests tab shows it
 * with everything ever asked for; the weekly list reuses it as the parts box
 * at the bottom (compact: only what is still waiting or on order), is told
 * when a request goes (onSent — that answers the list's parts line), and puts
 * its "Nothing needed this week" button right under the form (afterForm).
 */
function Requests({ readOnly, compact = false, onSent, afterForm }: {
  readOnly: boolean; compact?: boolean; onSent?: (item: string, qty: string) => void; afterForm?: React.ReactNode;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(['mechanic-requests'], () => api.get('/mechanic/requests').then(r => r.data));
  const [f, setF] = useState({ item: '', qty: '', urgency: 'normal', note: '' });
  const add = useMutation(() => api.post('/mechanic/requests', f).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Asked for');
      onSent?.(f.item.trim(), f.qty.trim());
      setF({ item: '', qty: '', urgency: 'normal', note: '' }); qc.invalidateQueries('mechanic-requests');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send it'); },
  });
  const mark = useMutation(({ id, status }: { id: number; status: string }) => api.patch(`/mechanic/requests/${id}`, { status }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Updated'); qc.invalidateQueries('mechanic-requests'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not update it'); },
  });

  const all: any[] = data?.requests || [];
  const shown = compact ? all.filter(r => r.status === 'open' || r.status === 'ordered') : all;

  return (
    <div className="space-y-3">
      <div className="card p-4 space-y-2">
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1"><Plus className="h-4 w-4" /> Need something?</p>
        <div className="flex gap-2">
          <input value={f.item} onChange={e => setF(p => ({ ...p, item: e.target.value }))} placeholder="Blades, bar oil, filter wrench…" className="input flex-1 text-sm" />
          <input value={f.qty} onChange={e => setF(p => ({ ...p, qty: e.target.value }))} placeholder="Qty" className="input w-20 text-sm" />
        </div>
        <input value={f.note} onChange={e => setF(p => ({ ...p, note: e.target.value }))} placeholder="Note (which machine, which store…)" className="input w-full text-sm" />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={f.urgency === 'need_now'} onChange={e => setF(p => ({ ...p, urgency: e.target.checked ? 'need_now' : 'normal' }))} />
          Need it now — can't work without it
        </label>
        <button onClick={() => add.mutate()} disabled={!f.item.trim() || add.isLoading || readOnly}
          className="w-full py-2.5 rounded-lg bg-brand-600 text-black text-sm font-bold disabled:opacity-50">{add.isLoading ? 'Sending…' : 'Ask the office'}</button>
      </div>

      {afterForm}

      {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : (
        <div className="card divide-y divide-gray-100 p-0">
          {shown.length === 0 && <p className="px-3 py-4 text-sm text-gray-500">{compact ? 'Nothing waiting on the office.' : 'Nothing asked for yet.'}</p>}
          {shown.map((r: any) => {
            const [word, cls] = STATUS_WORDS[r.status] || STATUS_WORDS.open;
            return (
              <div key={r.id} className="px-3 py-2.5 flex items-start justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{r.item}{r.qty ? ` × ${r.qty}` : ''}{r.urgency === 'need_now' && r.status === 'open' ? <span className="text-red-600 font-bold"> · now</span> : ''}</p>
                  <p className="text-xs text-gray-500">{r.requester || ''}{r.note ? ` · ${r.note}` : ''}{r.admin_note ? ` · office: ${r.admin_note}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{word}</span>
                  {r.status === 'ordered' && !readOnly && (
                    <button onClick={() => mark.mutate({ id: r.id, status: 'got_it' })} className="text-xs font-semibold text-green-700">Got it</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-gray-500">Requests go to the office's Needs Attention list. "Got it" is yours to tap when the part shows up.</p>
    </div>
  );
}

function BrokenGear({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(['mechanic-gear'], () => api.get('/mechanic/equipment').then(r => r.data));
  const [noteFor, setNoteFor] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const fix = useMutation(({ id, note: n }: { id: number; note: string }) => api.patch(`/mechanic/equipment/${id}/fixed`, { note: n }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Fixed'); setNoteFor(null); setNote(''); qc.invalidateQueries('mechanic-gear'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark it'); },
  });

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  const reports: any[] = data?.reports || [];
  return (
    <div className="space-y-2">
      {reports.length === 0 && <div className="card p-6 text-center text-sm text-gray-500"><Wrench className="h-6 w-6 mx-auto mb-1 text-gray-400" />Nothing broken. Nice.</div>}
      {reports.map(r => (
        <div key={r.id} className={`card p-3 ${r.severity === 'down' ? 'border-2 border-red-400' : ''}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                {r.severity === 'down' && <AlertTriangle className="h-4 w-4 text-red-600" />}
                {r.equipment_name}
                <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${r.severity === 'down' ? 'bg-red-100 text-red-800' : r.severity === 'major' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>{r.severity || 'minor'}</span>
              </p>
              {r.description && <p className="text-sm text-gray-600">{r.description}</p>}
              <p className="text-xs text-gray-500">{[r.reported_by, fmtDay(r.job_day || r.reported_at)].filter(Boolean).join(' · ')}</p>
            </div>
          </div>
          {noteFor === r.id ? (
            <div className="mt-2 space-y-2">
              <input value={note} onChange={e => setNote(e.target.value)} autoFocus placeholder="What you did (optional)" className="input w-full text-sm" />
              <div className="flex gap-2">
                <button onClick={() => fix.mutate({ id: r.id, note })} disabled={fix.isLoading} className="flex-1 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold disabled:opacity-50">Fixed</button>
                <button onClick={() => { setNoteFor(null); setNote(''); }} className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600">Cancel</button>
              </div>
            </div>
          ) : (
            !readOnly && <button onClick={() => setNoteFor(r.id)} className="mt-2 w-full py-2 rounded-lg border border-green-500 text-green-700 text-sm font-semibold">Mark fixed</button>
          )}
        </div>
      ))}
      <p className="text-[11px] text-gray-500">The crew report broken gear from their jobs; it lands here for you to fix and clear.</p>
    </div>
  );
}
